import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, ShieldCheck, Users, Zap, Palette, Play, ArrowRight,
  BarChart3, Target, LayoutGrid, Grid3x3, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const OPERANT_CHIPS = [
  { name: "Tact",        color: "#0284C7", bg: "#E0F2FE" },
  { name: "Echoic",      color: "#059669", bg: "#D1FAE5" },
  { name: "Intraverbal", color: "#9333EA", bg: "#F3E8FF" },
  { name: "Listener",    color: "#E11D48", bg: "#FFE4E6" },
  { name: "Textual",     color: "#4F46E5", bg: "#E0E7FF" },
  { name: "Imitation",   color: "#DB2777", bg: "#FCE7F3" },
];

export default function LandingPage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [demoing, setDemoing] = useState(false);

  const startDemo = async () => {
    setDemoing(true);
    try {
      const r = await http.post("/demo/create");
      if (refresh) await refresh();
      toast.success("Demo sandbox ready — you're logged in for 30 minutes.");
      // Small delay so cookie/user state settles before Protected renders
      setTimeout(() => nav("/learners"), 250);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start demo — please try again.");
    } finally {
      setDemoing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="landing-page">
      {/* Nav */}
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Discrimina" className="w-9 h-9 rounded-xl" />
          <div>
            <div className="font-display font-extrabold text-lg leading-none">Discrimina</div>
            <div className="text-[10px] tracking-[0.14em] uppercase text-slate-500">Visual Learning Platform</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => nav("/auth")}
            className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-3 py-2"
            data-testid="nav-login"
          >
            Log in
          </button>
          <Button onClick={() => nav("/auth?mode=register")} className="rounded-full h-10 px-4"
            data-testid="nav-signup">
            Start free
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-24 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Zero PHI leakage · HIPAA-aligned
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.02]">
            Run cleaner discrimination drills.
            <br />
            <span className="text-slate-500">In minutes, not spreadsheets.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-lg">
            The visual learning platform for modern BCBAs. Custom card libraries per learner, color-coded verbal
            operants, drag-to-match arrays and category sorting — all in one iPad-friendly workspace.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              onClick={startDemo}
              disabled={demoing}
              className="rounded-full h-12 px-6 text-base"
              data-testid="try-demo-button"
            >
              {demoing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Spinning up sandbox…</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Try the live demo</>
              )}
            </Button>
            <button
              onClick={() => nav("/auth?mode=register")}
              className="rounded-full h-12 px-6 text-base font-semibold text-slate-700 hover:bg-white border border-slate-200 inline-flex items-center gap-2"
              data-testid="cta-signup"
            >
              Create a real account <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {OPERANT_CHIPS.map((op) => (
              <span key={op.name}
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border-2"
                style={{ background: op.bg, borderColor: op.color, color: op.color }}
              >
                {op.name}
              </span>
            ))}
          </div>
        </div>

        {/* Right: mock preview */}
        <div className="relative">
          <div className="absolute -top-4 -left-4 w-24 h-24 rounded-3xl bg-emerald-100/70 blur-2xl" />
          <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-3xl bg-teal-100/70 blur-2xl" />
          <div className="relative bg-white border border-slate-200 rounded-3xl p-5 shadow-xl shadow-slate-200/60">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">
              Arrays · Drag to match
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Apple", op: "Tact",     bg: "#E0F2FE", color: "#0284C7" },
                { label: "Dog",   op: "Tact",     bg: "#E0F2FE", color: "#0284C7" },
                { label: "ah",    op: "Echoic",   bg: "#D1FAE5", color: "#059669" },
                { label: "Touch dog", op: "Listener", bg: "#FFE4E6", color: "#E11D48" },
              ].map((c) => (
                <div key={c.label}
                  className="rounded-xl border-2 p-4 h-32 flex flex-col justify-between"
                  style={{ borderColor: c.color, background: c.bg + "60" }}
                >
                  <div className="text-lg font-display font-bold" style={{ color: c.color }}>
                    {c.label}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full self-start"
                    style={{ background: c.color, color: "white" }}>
                    {c.op}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2 text-xs">
              <div className="flex-1 bg-emerald-100 text-emerald-700 rounded-lg py-2 text-center font-bold">✓ Matched!</div>
              <div className="flex-1 bg-slate-100 text-slate-600 rounded-lg py-2 text-center font-bold">Round 4 / 10</div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
          {[
            { Icon: LayoutGrid, title: "Drag-to-match arrays", body: "2–10 card arrays with drag-to-match interaction. Correct matches fade + celebrate. Wrong drops snap back with a gentle 'try again' cue." },
            { Icon: Palette, title: "Color-coded operants", body: "Tact, Echoic, Intraverbal, Listener, Textual, Imitation — never mix them up mid-session again." },
            { Icon: Grid3x3, title: "Category sorting", body: "Cards bucket into Animals, Foods, Objects, Vehicles and any custom category — perfect for teaching class discrimination." },
            { Icon: Users, title: "Team sharing", body: "Invite RBTs with role-based access (viewer / editor / owner)." },
            { Icon: ShieldCheck, title: "HIPAA safeguards", body: "Chart-name-only records, audit log, idle logout, 429 lockout — no PHI leaks." },
            { Icon: Zap, title: "AI stimulus photos", body: "Generate clean flashcard-ready photos for any tact in seconds." },
          ].map((f) => {
            const Icon = f.Icon;
            return (
              <div key={f.title} className="p-4">
                <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center text-slate-900 mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-display font-bold text-lg">{f.title}</div>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Pricing</div>
          <h2 className="font-display text-4xl font-extrabold">Simple plans. 7-day free trial.</h2>
          <p className="text-slate-600 mt-2">No card required to start. Cancel anytime.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <PricingCard
            testId="plan-free"
            name="Free"
            price="$0"
            note="forever"
            features={[
              "1 learner",
              "25 stimulus cards",
              "Card library + trial runner",
              "AI card generation (5/mo)",
            ]}
            cta="Start free"
            onClick={() => nav("/auth?mode=register&plan=free")}
          />
          <PricingCard
            testId="plan-solo"
            name="Solo BCBA"
            price="$19"
            note="per month · $15/mo billed yearly"
            features={[
              "Unlimited learners & cards",
              "Match / Sort activities",
              "PDF exports & cut sheets",
              "Parent consent flow",
              "Priority AI generation",
            ]}
            cta="Start 7-day trial"
            highlighted
            onClick={() => nav("/auth?mode=register&plan=solo")}
          />
          <PricingCard
            testId="plan-clinic"
            name="Clinic"
            price="$79"
            note="per month · $65/mo billed yearly · up to 10 seats"
            features={[
              "Everything in Solo",
              "Team sharing + role permissions",
              "Team-wide insights dashboard",
              "Weekly progress digest emails",
              "Audit log & compliance reports",
            ]}
            cta="Start 7-day trial"
            onClick={() => nav("/auth?mode=register&plan=clinic")}
          />
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-4xl font-extrabold">Ready to run a session?</h2>
          <p className="mt-3 text-slate-300 max-w-xl mx-auto">
            Fire up a demo sandbox — no signup — and take Discrimina for a spin with a preloaded learner.
          </p>
          <div className="mt-8 inline-flex flex-wrap gap-3 justify-center">
            <Button
              onClick={startDemo}
              disabled={demoing}
              className="rounded-full h-12 px-6 bg-white text-slate-900 hover:bg-slate-100"
              data-testid="footer-demo-button"
            >
              {demoing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Try the live demo
            </Button>
            <button
              onClick={() => nav("/auth?mode=register")}
              className="rounded-full h-12 px-6 border border-white/20 hover:bg-white/10 font-semibold"
            >
              Create an account
            </button>
          </div>
        </div>
        <div className="text-center text-xs text-slate-500 pb-6">
          © 2026 Discrimina · Built for BCBAs · <span className="opacity-70">Not medical advice.</span>
        </div>
      </section>
    </div>
  );
}

function PricingCard({ testId, name, price, note, features, cta, highlighted, onClick }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-6 flex flex-col ${
        highlighted
          ? "bg-slate-900 text-white border-slate-900 shadow-xl shadow-slate-300/60 -translate-y-2"
          : "bg-white text-slate-900 border-slate-200"
      }`}
    >
      <div className="text-xs font-bold uppercase tracking-[0.14em] opacity-70">{name}</div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-display text-4xl font-extrabold">{price}</span>
      </div>
      <div className={`text-xs mt-0.5 ${highlighted ? "text-slate-300" : "text-slate-500"}`}>{note}</div>
      <ul className="mt-5 space-y-2 text-sm flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className={`mt-1 w-1.5 h-1.5 rounded-full ${highlighted ? "bg-emerald-400" : "bg-emerald-500"}`} />
            <span className={highlighted ? "text-slate-100" : "text-slate-700"}>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onClick}
        className={`mt-6 rounded-full h-11 ${highlighted ? "bg-white text-slate-900 hover:bg-slate-100" : ""}`}
        data-testid={`${testId}-cta`}
      >
        {cta}
      </Button>
    </div>
  );
}
